import { handleSettledPromises } from '@kk/shared/utils/async';
import { assertParamsPrescence } from '@kk/shared/utils/validators';
import { useRouteLoaderData } from 'react-router';
import api from '#/api';
import type { RequestOpts } from '#/api/api-types';
import { assertNoLoaderErrors } from '#/api/errors';
import { fetchCompanyInfoQuery } from '#/api/hooks/useCompanyInfo';
import { fetchCreditInfoQuery } from '#/api/hooks/useCreditInfo';
import { fetchGuarantorLiabilityQuery } from '#/api/hooks/useGuarantorLiability';
import { fetchLoanInfoQuery } from '#/api/hooks/useLoanInfo';
import type { InferLoaderData } from '#/routes/types';

export type LoaderData = InferLoaderData<typeof loader>;

export type Params = {
  contractId?: string;
};

export const id = 'contractPage' as const;

async function loader({
  params,
  opts,
}: {
  params: Params;
  opts?: RequestOpts;
}) {
  assertParamsPrescence(params);

  if (!params.contractId) {
    throw new Error('Contract ID is required');
  }

  const data = await handleSettledPromises([
    fetchLoanInfoQuery(params.contractId, opts),
    fetchGuarantorLiabilityQuery({ contractId: params.contractId }, opts),
    fetchCompanyInfoQuery(opts),
  ]);

  assertNoLoaderErrors(id, data);

  const [loanInfo, guarantorLiability, companyInfo] = data;

  const dependentRequests = await handleSettledPromises([
    loanInfo.loanProduct === api.ContractLoanTypeEnum.Byggekredit
      ? fetchCreditInfoQuery({ contractId: params.contractId }, opts)
      : Promise.resolve(null),
  ]);
  assertNoLoaderErrors(id, dependentRequests);

  const [creditInfo] = dependentRequests;

  return { loanInfo, guarantorLiability, companyInfo, creditInfo };
}

export const useContractPageLoaderData = () =>
  useRouteLoaderData(id) as LoaderData;

export default loader;
